import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Globe, Moon, Server, X } from 'lucide-react';
import { useTheme } from '../lib/ThemeContext';
import { useLanguage } from '../lib/LanguageContext';

const MCP_URL = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/mcp`;

function Section({ icon: Icon, title, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <Icon className="w-3.5 h-3.5 text-keep-textMuted" />
        <h3 className="text-[12px] text-keep-textMuted uppercase tracking-wide">{title}</h3>
      </div>
      <div className="rounded-xl border border-keep-border bg-keep-surface overflow-hidden">{children}</div>
    </section>
  );
}

function OptionRow({ label, active, onClick, testid, isLast }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3 text-left text-[14px] text-keep-text hover:bg-keep-hover ${
        isLast ? '' : 'border-b border-keep-border'
      }`}
      data-testid={testid}
    >
      {label}
      {active ? <Check className="w-[18px] h-[18px] text-keep-accent" /> : null}
    </button>
  );
}

/** Web counterpart of mobile/app/settings.js: language, theme, MCP URL. */
export default function SettingsModal({ onClose }) {
  const { theme, setTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(copiedTimerRef.current);
    };
  }, [onClose]);

  const copyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // Portaled to <body> so it isn't trapped under the sticky header's stacking
  // context; pointerdown is stopped so the grid's marquee-select handler
  // (React events still bubble through portals) never sees it.
  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center pt-16 md:pt-24 px-4 pb-6 overflow-y-auto"
      onClick={onClose}
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="settings-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-keep-page border border-keep-border rounded-2xl shadow-keepHover animate-fade-in"
        data-testid="settings-modal"
      >
        <div className="h-14 pl-5 pr-2 flex items-center justify-between border-b border-keep-border">
          <h2 id="settings-title" className="text-[16px] font-medium text-keep-text">
            {t('settings.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-keep-hover"
            aria-label={t('common.close')}
            data-testid="settings-close"
          >
            <X className="w-5 h-5 text-keep-textMuted" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          <Section icon={Globe} title={t('settings.language')}>
            <div role="radiogroup" aria-label={t('settings.language')}>
              <OptionRow
                label={t('settings.english')}
                active={language === 'en'}
                onClick={() => setLanguage('en')}
                testid="settings-language-en"
              />
              <OptionRow
                label={t('settings.indonesian')}
                active={language === 'id'}
                onClick={() => setLanguage('id')}
                testid="settings-language-id"
                isLast
              />
            </div>
          </Section>

          <Section icon={Moon} title={t('settings.theme')}>
            <div role="radiogroup" aria-label={t('settings.theme')}>
              <OptionRow
                label={t('settings.light')}
                active={theme === 'light'}
                onClick={() => setTheme('light')}
                testid="settings-theme-light"
              />
              <OptionRow
                label={t('settings.dark')}
                active={theme === 'dark'}
                onClick={() => setTheme('dark')}
                testid="settings-theme-dark"
                isLast
              />
            </div>
          </Section>

          <Section icon={Server} title={t('settings.mcpTitle')}>
            <div className="px-4 py-3.5 space-y-2.5">
              <p className="text-[12.5px] text-keep-textMuted leading-relaxed">{t('settings.mcpDesc')}</p>
              <button
                type="button"
                onClick={copyMcpUrl}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-keep-searchBg hover:opacity-80 text-left"
                data-testid="settings-mcp-copy"
              >
                <span className="flex-1 min-w-0 truncate text-[12.5px] text-keep-text font-mono">{MCP_URL}</span>
                {copied ? (
                  <span className="flex items-center gap-1 text-[11px] text-keep-accentDark flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-keep-accent" />
                    {t('settings.copied')}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[11px] text-keep-textMuted flex-shrink-0">
                    <Copy className="w-3.5 h-3.5" />
                    {t('settings.copy')}
                  </span>
                )}
              </button>
            </div>
          </Section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
